/**
 * DailyVideoCall - Embeds Daily Prebuilt video call
 * 
 * Uses Daily's prebuilt UI for:
 * - Clean, responsive video layout
 * - Built-in controls (mute, camera toggle, leave)
 * - Privacy enforced via token (no recording, no screenshare)
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';

/**
 * @param {Object} props
 * @param {string} props.roomUrl - Daily room URL (e.g., https://unitylove.daily.co/ul-xxx)
 * @param {string} props.token - Meeting token with privacy restrictions
 * @param {() => void} [props.onLeave] - Callback when user leaves
 * @param {() => void} [props.onJoined] - Callback when user joins successfully
 * @param {(error: Error) => void} [props.onError] - Callback on error
 * @param {string} [props.className] - Additional CSS classes
 */
export default function DailyVideoCall({
  roomUrl,
  token,
  onLeave,
  onJoined,
  onError,
  className = '',
}) {
  const containerRef = useRef(null);
  const callFrameRef = useRef(null);
  const [status, setStatus] = useState('initializing'); // initializing | joining | joined | left | error
  const [errorMessage, setErrorMessage] = useState('');

  const destroyCall = useCallback(() => {
    if (callFrameRef.current) {
      callFrameRef.current.destroy();
      callFrameRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!roomUrl || !token || !containerRef.current) return;

    // Prevent re-initialization
    if (callFrameRef.current) return;

    const callFrame = DailyIframe.createFrame(containerRef.current, {
      iframeStyle: {
        width: '100%',
        height: '100%',
        border: '0',
        borderRadius: '16px',
      },
      showLeaveButton: true,
      showFullscreenButton: true,
    });

    callFrameRef.current = callFrame;

    // Event handlers - these are async callbacks, so setState is safe here
    callFrame.on('joining-meeting', () => {
      setStatus('joining');
    });

    callFrame.on('joined-meeting', () => {
      setStatus('joined');
      onJoined?.();
    });

    callFrame.on('left-meeting', () => {
      setStatus('left');
      onLeave?.();
    });

    callFrame.on('error', (e) => {
      console.error('[DailyVideoCall] Error:', e);
      setStatus('error');
      setErrorMessage(e.errorMsg || 'Failed to connect to video call');
      onError?.(new Error(e.errorMsg || 'Video call error'));
    });

    // Join the call
    callFrame
      .join({ url: roomUrl, token })
      .catch((err) => {
        console.error('[DailyVideoCall] Join error:', err);
        setStatus('error');
        setErrorMessage(err.message || 'Failed to join video call');
        onError?.(err);
      });

    // Cleanup on unmount
    return () => {
      destroyCall();
    };
    // Dependencies: roomUrl and token are the primary triggers.
    // destroyCall, onJoined, onLeave, onError are stable callbacks.
  }, [roomUrl, token, destroyCall, onJoined, onLeave, onError]);

  // Leave button handler
  const handleLeave = useCallback(() => {
    if (callFrameRef.current) {
      callFrameRef.current.leave();
    }
  }, []);

  return (
    <div 
      className={`relative w-full ${className}`}
      data-testid="daily-call-container"
      data-call-status={status}
    >
      {/* Status overlay for loading/error states */}
      {status === 'initializing' && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl z-10"
          data-testid="daily-call-initializing"
        >
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-pink-500 rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-white/70 text-sm">Initializing video...</p>
          </div>
        </div>
      )}

      {status === 'joining' && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl z-10"
          data-testid="daily-call-joining"
        >
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-white/20 border-t-pink-500 rounded-full animate-spin mx-auto" />
            <p className="mt-4 text-white/70 text-sm">Connecting to your date...</p>
          </div>
        </div>
      )}

      {status === 'error' && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl z-10"
          data-testid="daily-call-error"
        >
          <div className="text-center p-6">
            <div className="text-4xl mb-4">😕</div>
            <p className="text-red-400 text-sm mb-4">{errorMessage}</p>
            <button
              onClick={onLeave}
              className="px-4 py-2 rounded-full bg-white/10 text-white text-sm hover:bg-white/20 transition"
              data-testid="daily-call-error-back-btn"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {status === 'left' && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-2xl z-10"
          data-testid="daily-call-left"
        >
          <div className="text-center p-6">
            <div className="text-4xl mb-4">👋</div>
            <p className="text-white/70 text-sm mb-4">You&apos;ve left the video call</p>
            <button
              onClick={onLeave}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 text-white text-sm hover:brightness-110 transition"
              data-testid="daily-call-continue-btn"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Daily video container - mounted when call is active */}
      <div
        ref={containerRef}
        className="w-full aspect-video bg-black/40 rounded-2xl overflow-hidden"
        style={{ minHeight: '300px' }}
        data-testid={status === 'joined' ? 'daily-call-mounted' : 'daily-call-container-inner'}
      />

      {/* Manual leave button (backup) */}
      {status === 'joined' && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={handleLeave}
            className="px-6 py-2 rounded-full border border-white/20 text-white/80 text-sm hover:bg-white/10 transition"
            data-testid="daily-call-leave-btn"
          >
            Leave Video Call
          </button>
        </div>
      )}
    </div>
  );
}
